/**
 * Token Manager Module
 *
 * Handles MSAL token acquisition, JWT parsing, and error handling.
 * Provides wrapper functions for safe token operations without exposing sensitive data.
 */

import { getMsalInstance } from '@/authConfig'
import {
  AuthError,
  BrowserAuthError,
  ServerError,
  ClientAuthError,
  InteractionRequiredAuthError,
} from '@azure/msal-browser'
import type { AuthenticationResult } from '@azure/msal-browser'
import { AppLogger } from '@/lib/logger'

const log = AppLogger.getLogger('lib/tokenManager')

// ============================================================================
// Types
// ============================================================================

export interface TokenResponse {
  token: string
  expiresAt: number  // milliseconds
  expiresIn: number  // seconds remaining
  email?: string
  name?: string
}

export interface MsalErrorResult {
  code: string
  message: string
  shouldTerminate: boolean
}

export interface JwtPayload {
  exp?: number
  sub?: string
  email?: string
  name?: string
  [key: string]: any
}

// ============================================================================
// JWT Parsing
// ============================================================================

/**
 * Safely parse JWT payload without validation
 *
 * This function decodes a JWT token to extract claims (like exp) without
 * validating the signature. Used only for extracting expiration times.
 *
 * @param token - JWT token string
 * @returns Decoded JWT payload or null if parsing fails
 */
export function parseJwtPayload(token: string): JwtPayload | null {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) {
      return null
    }

    // JWT uses base64url encoding (- instead of +, _ instead of /)
    // Convert to standard base64 before decoding
    const payload = parts[1]
      .replace(/-/g, '+')
      .replace(/_/g, '/')
    const decoded = atob(payload)
    return JSON.parse(decoded)
  } catch {
    return null
  }
}

/**
 * Extract expiration time from JWT token
 *
 * @param token - JWT token string
 * @returns Expiration timestamp in milliseconds, or null if not found
 */
function extractTokenExpiration(token: string): number | null {
  const payload = parseJwtPayload(token)
  if (!payload?.exp) {
    return null
  }

  // Convert from seconds (JWT standard) to milliseconds
  return payload.exp * 1000
}

// ============================================================================
// MSAL Error Handling
// ============================================================================

/**
 * Handle MSAL errors with appropriate error codes and termination flags
 *
 * Maps MSAL error types to application error codes for consistent handling.
 * Never logs token details to prevent security issues.
 *
 * @param error - MSAL error object
 * @returns Structured error result with code and termination flag
 */
export function handleMsalError(error: AuthError | Error): MsalErrorResult {
  if (error instanceof InteractionRequiredAuthError) {
    // User must re-authenticate - permanent failure for current context
    log.error('InteractionRequiredAuthError: User must re-authenticate', {
      errorCode: (error as any).errorCode,
    })
    return {
      code: 'reauth_required',
      message: 'Your credentials have been updated or consent was revoked. Please sign in again.',
      shouldTerminate: true,
    }
  }

  if (error instanceof BrowserAuthError) {
    // Browser storage or network issue
    log.error('BrowserAuthError: Browser storage or network issue', {
      errorCode: (error as any).errorCode,
    })
    return {
      code: 'browser_error',
      message: 'A browser error occurred. Please try again.',
      shouldTerminate: true,
    }
  }

  if (error instanceof ServerError) {
    // Azure AD service error
    log.error('ServerError: Azure AD service issue', {
      errorCode: (error as any).errorCode,
    })
    return {
      code: 'service_unavailable',
      message: 'Authentication service is temporarily unavailable. Please try again.',
      shouldTerminate: true,
    }
  }

  if (error instanceof ClientAuthError) {
    // Configuration or request error
    log.error('ClientAuthError: Configuration issue', {
      errorCode: (error as any).errorCode,
    })
    return {
      code: 'config_error',
      message: 'Configuration error. Please contact support.',
      shouldTerminate: true,
    }
  }

  // Unknown error type
  log.error('Unknown auth error', {
    type: error.constructor.name,
  })
  return {
    code: 'unknown_error',
    message: 'An unexpected error occurred. Please try again.',
    shouldTerminate: true,
  }
}

// ============================================================================
// Token Acquisition
// ============================================================================

/**
 * Silently acquire an access token from MSAL
 *
 * Attempts to get a valid token without user interaction. If the token
 * has expired or needs re-authentication, MSAL will throw
 * InteractionRequiredAuthError which should be caught by the caller.
 *
 * @returns TokenResponse with token and expiration details
 * @throws AuthError (or subclasses) on failure
 */
export async function acquireTokenSilently(): Promise<TokenResponse> {
  const msalInstance = getMsalInstance()

  try {
    const response: AuthenticationResult = await msalInstance.acquireTokenSilent({
      scopes: ['openid', 'profile', 'email'],
    })

    // Extract expiration time from token
    const expiresAt = extractTokenExpiration(response.accessToken)
    const now = Date.now()
    const expiresIn = expiresAt ? Math.floor((expiresAt - now) / 1000) : 0

    // Extract user claims from token
    const email = (response.idTokenClaims as any)?.email as string | undefined
    const name = (response.idTokenClaims as any)?.name as string | undefined

    return {
      token: response.accessToken,
      expiresAt: expiresAt || (now + 3600000), // Default to 1 hour if not found
      expiresIn: Math.max(0, expiresIn),
      email,
      name,
    }
  } catch (error) {
    // Let caller handle specific error types
    throw error
  }
}

/**
 * Get safe error message for token acquisition failures
 *
 * @param error - MSAL error object
 * @returns User-friendly error message
 */
export function getTokenErrorMessage(error: AuthError | Error): string {
  const result = handleMsalError(error)
  return result.message
}
